import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IAssignShiftResponse,
  IGetAllWorkSchedulesSingleResponse,
} from 'types/api/workScheduleApi.interface'
import { IAddArraySchedule, IAddSchedule, IWorkScheduleSlice } from 'types/workSchedule.interface'
import { baseUrl } from 'utils/axios'
import { areDatesSameDay, formatParams, handleErrors } from 'utils/helpers'

const initialState: IWorkScheduleSlice = {
  isLoading: false,
  workSchedules: [],
  totalCount: 0,
  totalPages: 0,
  aiRule: null,
}

export const getWorkSchedules = createAsyncThunk(
  'workSchedules/getWorkSchedules',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllWorkSchedulesSingleResponse>> =
        await baseUrl.get('work-schedule', { params })
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getWorkSchedulesAiRule = createAsyncThunk(
  'workSchedules/getWorkSchedulesAiRule',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<{ rule: string }[]> = await baseUrl.get('work-schedule/ai-rule')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addWorkSchedulesAiRule = createAsyncThunk(
  'workSchedules/addWorkSchedulesAiRule',
  async (rule: { rule: string }, thunkAPI) => {
    try {
      await baseUrl.post('work-schedule/ai-rule', rule)
      return rule.rule
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addWorkSchedule = createAsyncThunk(
  'addWorkSchedule/workSchedules',
  async (schedule: IAddSchedule, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAssignShiftResponse> = await baseUrl.post(
        'work-schedule/user/' + schedule.userId,
        schedule,
        thunkAPI,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const removeWorkSchedule = createAsyncThunk(
  'removeWorkSchedule/workSchedules',
  async (schedule: { userId: number; date: any }, thunkAPI) => {
    try {
      await baseUrl.delete(
        'work-schedule/user/' + schedule.userId + '/day/' + schedule.date.toISOString(),
        thunkAPI,
      )
      return schedule
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addWorkScheduleArray = createAsyncThunk(
  'addWorkScheduleArray/workSchedules',
  async (schedule: IAddArraySchedule, thunkAPI) => {
    try {
      const resp = await baseUrl.post('work-schedule/users', schedule, thunkAPI)

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const workSchedulesSlice = createSlice({
  name: 'workSchedules',
  initialState,
  reducers: {
    clearWorkSchedule: (state) => {
      state.workSchedules = []
      state.totalPages = 0
      state.totalCount = 0
    },
    clearAiRules: (state) => {
      state.aiRule = null
    },
  },
  extraReducers: {
    [getWorkSchedules.pending.type]: (state) => {
      state.isLoading = true
    },
    [getWorkSchedules.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.workSchedules = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getWorkSchedules.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getWorkSchedulesAiRule.pending.type]: (state) => {
      state.isLoading = true
    },
    [getWorkSchedulesAiRule.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.aiRule = payload
    },
    [getWorkSchedulesAiRule.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addWorkSchedulesAiRule.pending.type]: (state) => {
      state.isLoading = true
    },
    [addWorkSchedulesAiRule.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.aiRule.rule = payload
      toast.success('Rule added successfully.')
    },
    [addWorkSchedulesAiRule.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [removeWorkSchedule.pending.type]: (state) => {
      state.isLoading = true
    },
    [removeWorkSchedule.fulfilled.type]: (
      state,
      { payload }: { payload: { userId: number; date: any } },
    ) => {
      const index = state.workSchedules.findIndex((ws) => ws.id == payload.userId)
      const newWS = [...state.workSchedules]
      const shiftId = newWS[index].schedules.find((s) => areDatesSameDay(s.date, payload.date))
        .shift.id
      newWS[index].schedules = newWS[index].schedules.filter(
        (s) => !(s.shift.id === shiftId && areDatesSameDay(s.date, payload.date)),
      )
      state.workSchedules = newWS
      toast.success('Schedule successfully removed.')
      state.isLoading = false
    },
    [removeWorkSchedule.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addWorkSchedule.fulfilled.type]: (state, { payload }: { payload: IAssignShiftResponse }) => {
      toast.success('Schedule added')
      state.isLoading = false
      const foundIndex = state.workSchedules.findIndex((item) => item.id === payload.userId)
      if (
        state.workSchedules[foundIndex].schedules.some(
          (s) => new Date(s.date).getTime() === new Date(payload.date).getTime(),
        )
      ) {
        const foundScheduleIndex = state.workSchedules[foundIndex].schedules.findIndex(
          (s) => new Date(s.date).getTime() === new Date(payload.date).getTime(),
        )
        state.workSchedules[foundIndex].schedules[foundScheduleIndex] = {
          date: payload.date,
          shift: payload.shift,
          id: payload.id,
          isOutHouse: payload.isOutHouse,
        }
      } else {
        state.workSchedules[foundIndex].schedules.push({
          date: payload.date,
          shift: payload.shift,
          isOutHouse: payload.isOutHouse,
          id: payload.id,
        })
      }
    },
    [addWorkSchedule.pending.type]: (state) => {
      state.isLoading = true
    },
    [addWorkSchedule.rejected.type]: (state, { payload }) => {
      const msg = payload?.response?.data?.message
      if (typeof msg === 'string' && msg.toLowerCase().includes('unassign') && msg.toLowerCase().includes('past')) {
        toast.error('Cannot assign shift in the past')
      } else {
        handleErrors(payload)
      }
      state.isLoading = false
    },
    [addWorkScheduleArray.fulfilled.type]: (state, { payload }) => {
      toast.success('Schedule added')
      state.isLoading = false
      for (const shift of payload) {
        const foundIndex = state.workSchedules.findIndex((s) => s.id === shift.userId)
        const singleShiftIndex = state.workSchedules[foundIndex].schedules.findIndex(
          (s) => new Date(s.date).getTime() === new Date(shift.date).getTime(),
        )
        if (singleShiftIndex < 0) {
          state.workSchedules[foundIndex].schedules.push(shift)
        } else {
          state.workSchedules[foundIndex].schedules[singleShiftIndex] = shift
        }
      }
    },
    [addWorkScheduleArray.pending.type]: (state) => {
      state.isLoading = true
    },
    [addWorkScheduleArray.rejected.type]: (state, { payload }) => {
      const msg = payload?.response?.data?.message
      if (typeof msg === 'string' && msg.toLowerCase().includes('unassign') && msg.toLowerCase().includes('past')) {
        toast.error('Cannot assign shift in the past')
      } else {
        handleErrors(payload)
      }
      state.isLoading = false
    },
  },
})

export const { clearWorkSchedule, clearAiRules } = workSchedulesSlice.actions

export default workSchedulesSlice.reducer
